// disruption-overview — pure helpers shared by the guided-resolution rail
// (resolution-run.tsx) and stepper (resolution-stepper.tsx): step labels/hints/
// icons, status-chip tone and the "done/total" counter copy. Kept out of the
// component files so React Fast Refresh keeps working (components-only exports).
//
// THIS FILE HOLDS i18n KEYS, NEVER LANGUAGE. The tables below map an op / a
// status to a key of the `rebooking` namespace; the helpers resolve it through a
// translator. Every helper takes that translator as its LAST, optional argument:
// a component passes the `t` from `useTranslation('rebooking')` (whose identity
// changes on `languageChanged`, so a memoised consumer redraws in the new
// language), and callers with no React context (tests, the Tower's shift
// summary, the frozen-signature helpers) fall through to `rebookingT` — bound to
// the namespace only, never to a language, so it always resolves against
// whatever language the console is showing right now. Same contract as
// `tower/tower-i18n.ts`.

import i18next, { type TFunction } from 'i18next';
import {
  BedDouble,
  Bus,
  CheckCircle2,
  CircleDashed,
  HelpCircle,
  Loader2,
  Lock,
  MinusCircle,
  Plane,
  ShieldCheck,
  Ticket,
  TriangleAlert,
  UserCheck,
  UtensilsCrossed,
} from 'lucide-react';
import type {
  DisruptionGroupFacts,
  ResolutionOp,
  ResolutionStep,
  ResolutionStepStatus,
} from '@/lib/disruptions/types';

export type RebookingT = TFunction<'rebooking'>;

/** Resolves a key of the `rebooking` namespace against the console's live language. */
const rebookingT: RebookingT = i18next.getFixedT(null, 'rebooking');

/** The ACTION each step offers — a verb, only ever on a button or a "next" line. */
export const STEP_LABEL_KEY: Record<ResolutionOp, string> = {
  rebooking: 'steps.label.rebooking',
  tickets: 'steps.label.tickets',
  hotels: 'steps.label.hotels',
  vouchers: 'steps.label.vouchers',
  acceptance: 'steps.label.acceptance',
  // MASS ground transport (additive op).
  transfers: 'steps.label.transfers',
  contain: 'steps.label.contain',
};

/** What each step is ABOUT — the noun the operator reads a state against
 *  ("Hotels: booked"), never the verb of the action. */
export const STEP_SUBJECT_KEY: Record<ResolutionOp, string> = {
  rebooking: 'steps.subject.rebooking',
  tickets: 'steps.subject.tickets',
  hotels: 'steps.subject.hotels',
  vouchers: 'steps.subject.vouchers',
  acceptance: 'steps.subject.acceptance',
  transfers: 'steps.subject.transfers',
  contain: 'steps.subject.contain',
};
export function stepSubject(op: string, t: RebookingT = rebookingT): string {
  const key = STEP_SUBJECT_KEY[op as ResolutionOp];
  return key ? t(key) : op;
}

/** The state of a step as the operator should read it: a short STATE label
 *  ("Booked · unconfirmed") plus a one-line factual detail. Derived from the
 *  step (status/counters) and, where the status alone is ambiguous, from the
 *  domain facts (held vs confirmed, ticketed, declined). Verbs live only on
 *  buttons/confirmations — a finished step never reads as a pending action. */
export function stepState(
  step: ResolutionStep,
  facts?: DisruptionGroupFacts | null,
  siblings?: readonly ResolutionStep[],
  t: RebookingT = rebookingT,
): { label: string; detail: string } {
  const done = step.done ?? 0;
  const total = step.total ?? 0;
  const remaining = step.remaining ?? Math.max(0, total - done);
  const gaps = step.gaps ?? 0;
  const g = facts && facts.known?.cases ? facts : null;
  const sib = (op: ResolutionOp) => siblings?.find((x) => x.op === op);

  if (step.status === 'UNKNOWN')
    return {
      label: t('steps.state.unknown.label'),
      detail: step.reason ?? t('steps.state.unknown.detail'),
    };
  if (step.status === 'BLOCKED') {
    const label =
      step.op === 'tickets'
        ? t('steps.state.blocked.tickets')
        : step.op === 'hotels' || step.op === 'vouchers'
          ? t('steps.state.blocked.determined')
          : step.op === 'acceptance'
            ? t('steps.state.blocked.acceptance')
            : step.op === 'contain'
              ? step.containMode === 'TRAVELLING'
                ? t('steps.state.blocked.containTravelling')
                : t('steps.state.contain.partial')
              : t('steps.state.blocked.fallback');
    return { label, detail: step.reason ?? '' };
  }

  switch (step.op) {
    case 'rebooking': {
      // Two very different skips: "nobody lost a flight" vs "this station's policy
      // has the engine off". The second is a governance fact the operator must be
      // able to read (and check) — never a generic "no rebooking needed".
      if (step.status === 'SKIPPED') {
        return step.policyDisabled
          ? {
              label: t('steps.offByPolicy'),
              detail: step.reason ?? t('steps.state.rebooking.offByPolicyDetail'),
            }
          : { label: t('steps.state.rebooking.notNeeded'), detail: step.reason ?? '' };
      }
      if (step.status === 'RUNNING')
        return {
          label: t('steps.state.rebooking.running', { done, total }),
          detail: step.reason ?? t('steps.state.rebooking.awaitFlight', { count: remaining }),
        };
      if (step.status === 'PENDING')
        return {
          label: t('steps.state.rebooking.pending'),
          detail: t('steps.state.rebooking.awaitFlight', { count: total }),
        };
      if (step.status === 'PARTIAL') {
        if (remaining > 0)
          return {
            label: t('steps.state.rebooking.partial'),
            detail: t('steps.state.rebooking.partialDetail', {
              done,
              total,
              remaining,
              gaps: gaps > 0 ? t('steps.state.rebooking.partialGaps', { count: gaps }) : '',
            }),
          };
        return {
          label: t('steps.state.rebooking.exceptions'),
          detail: t('steps.state.rebooking.exceptionsDetail', { done, total, gaps }),
        };
      }
      // DONE — reversible selection until the tickets are issued.
      const ticketsDone = sib('tickets')?.status === 'DONE';
      return ticketsDone
        ? {
            label: t('steps.state.rebooking.done'),
            detail: t('steps.state.rebooking.doneDetail', {
              count: done,
              pax: g ? t('steps.state.rebooking.donePax', { count: g.assignedPax }) : '',
            }),
          }
        : {
            label: t('steps.state.rebooking.selected'),
            detail: t('steps.state.rebooking.selectedDetail', { done, total }),
          };
    }
    case 'tickets': {
      if (step.status === 'SKIPPED')
        return { label: t('steps.state.tickets.skipped'), detail: step.reason ?? '' };
      if (step.status === 'RUNNING')
        return { label: t('steps.issuing'), detail: step.reason ?? '' };
      if (step.status === 'PENDING')
        return {
          label: t('steps.state.tickets.pending'),
          detail: t('steps.state.tickets.pendingDetail', { count: total }),
        };
      if (step.status === 'PARTIAL')
        return {
          label: t('steps.state.tickets.partial'),
          detail: t('steps.state.tickets.partialDetail', { done, total, count: remaining }),
        };
      return {
        label: t('steps.state.tickets.done'),
        detail: t('steps.state.tickets.doneDetail', { done, total }),
      };
    }
    case 'hotels': {
      if (step.status === 'SKIPPED')
        return {
          label: t('steps.notNeeded'),
          detail: step.reason ?? t('steps.state.hotels.skippedDetail'),
        };
      if (step.status === 'RUNNING')
        return { label: t('steps.state.hotels.running', { done, total }), detail: step.reason ?? '' };
      if (step.status === 'PENDING')
        return {
          label: t('steps.state.hotels.pending'),
          detail: t('steps.state.hotels.pendingDetail', {
            count: total,
            undecided:
              g && g.hotelUndecided > 0
                ? t('steps.state.hotels.pendingUndecided', { count: g.hotelUndecided })
                : '',
          }),
        };
      if (step.status === 'PARTIAL')
        return {
          label: t('steps.state.hotels.partial'),
          detail: t('steps.state.hotels.partialDetail', {
            done,
            total,
            unbooked:
              remaining > 0 ? t('steps.state.hotels.partialUnbooked', { count: remaining }) : '',
            gaps: gaps > 0 ? t('steps.state.hotels.partialGaps', { count: gaps }) : '',
          }),
        };
      // DONE — held (reversible, awaiting acceptance) vs confirmed.
      if (g && g.hotelConfirmed >= total && total > 0)
        return {
          label: t('steps.state.hotels.confirmed'),
          detail: t('steps.state.hotels.confirmedDetail', { done: g.hotelConfirmed, total }),
        };
      if (g && g.hotelConfirmed > 0)
        return {
          label: t('steps.state.hotels.partlyUnconfirmed'),
          detail: t('steps.state.hotels.partlyUnconfirmedDetail', {
            count: g.hotelConfirmed,
            held: g.hotelHeld,
          }),
        };
      return {
        label: t('steps.state.hotels.unconfirmed'),
        detail: t('steps.state.hotels.unconfirmedDetail', { done, total }),
      };
    }
    case 'vouchers': {
      if (step.status === 'SKIPPED')
        return {
          label: t('steps.notApplicable'),
          detail: step.reason ?? t('steps.state.vouchers.skippedDetail'),
        };
      if (step.status === 'RUNNING')
        return { label: t('steps.issuing'), detail: step.reason ?? '' };
      if (step.status === 'PENDING')
        return {
          label: t('steps.state.vouchers.pending'),
          detail: t('steps.state.vouchers.pendingDetail', { count: total }),
        };
      if (step.status === 'PARTIAL')
        return {
          label: t('steps.state.vouchers.partial'),
          detail: t('steps.state.vouchers.partialDetail', {
            done,
            total,
            gaps: gaps > 0 ? t('steps.state.vouchers.partialGaps', { count: gaps }) : '',
          }),
        };
      return {
        label: t('steps.state.vouchers.done'),
        detail: t('steps.state.vouchers.doneDetail', { done, total }),
      };
    }
    case 'acceptance': {
      if (step.status === 'SKIPPED')
        return { label: t('steps.state.acceptance.skipped'), detail: step.reason ?? '' };
      if (step.status === 'RUNNING')
        return { label: t('steps.state.acceptance.running'), detail: step.reason ?? '' };
      if (step.status === 'PENDING')
        return {
          label: t('steps.state.acceptance.pending'),
          detail: t('steps.state.acceptance.pendingDetail', {
            done,
            total,
            declined:
              g && g.declined > 0
                ? t('steps.state.acceptance.declinedSuffix', { count: g.declined })
                : '',
          }),
        };
      if (step.status === 'PARTIAL')
        return {
          label: t('steps.state.acceptance.partial'),
          detail: t('steps.state.acceptance.partialDetail', {
            done,
            total,
            declined:
              gaps > 0 ? t('steps.state.acceptance.declinedSuffix', { count: gaps }) : '',
            waiting:
              remaining > 0
                ? t('steps.state.acceptance.partialWaiting', { count: remaining })
                : '',
          }),
        };
      return {
        label: t('steps.state.acceptance.done'),
        detail: t('steps.state.acceptance.doneDetail', { done, total }),
      };
    }
    case 'transfers': {
      // MASS ground transport (additive step).
      if (step.status === 'SKIPPED')
        return {
          label: t('steps.notNeeded'),
          detail: step.reason ?? t('steps.state.transfers.skippedDetail'),
        };
      if (step.status === 'RUNNING')
        return {
          label: t('steps.state.transfers.running', { done, total }),
          detail: step.reason ?? '',
        };
      if (step.status === 'PENDING')
        return {
          label: t('steps.state.transfers.pending'),
          detail: t('steps.state.transfers.pendingDetail', { count: total }),
        };
      if (step.status === 'PARTIAL')
        return {
          label: t('steps.state.transfers.partial'),
          detail: t('steps.state.transfers.partialDetail', {
            done,
            total,
            onTheWay:
              remaining > 0
                ? t('steps.state.transfers.partialOnTheWay', { count: remaining })
                : '',
            gaps: gaps > 0 ? t('steps.state.transfers.partialGaps', { count: gaps }) : '',
          }),
        };
      return {
        label: t('steps.state.transfers.done'),
        detail: t('steps.state.transfers.doneDetail', { done, total }),
      };
    }
    case 'contain':
    default: {
      if (step.status === 'SKIPPED')
        return { label: t('steps.notApplicable'), detail: step.reason ?? '' };
      if (step.status === 'RUNNING') return { label: t('steps.state.contain.running'), detail: '' };
      if (step.status === 'PENDING') {
        return step.containMode === 'NATURAL'
          ? {
              label: t('steps.state.contain.natural'),
              detail: step.reason ?? t('steps.state.contain.naturalDetail'),
            }
          : {
              label: t('steps.state.contain.forced'),
              detail: step.reason ?? t('steps.state.contain.forcedDetail'),
            };
      }
      if (step.status === 'PARTIAL')
        return { label: t('steps.state.contain.partial'), detail: step.reason ?? '' };
      return {
        label: t('steps.state.contain.done'),
        detail: t('steps.state.contain.doneDetail'),
      };
    }
  }
}

export const STEP_HINT_KEY: Record<ResolutionOp, string> = {
  rebooking: 'steps.hint.rebooking',
  tickets: 'steps.hint.tickets',
  hotels: 'steps.hint.hotels',
  vouchers: 'steps.hint.vouchers',
  acceptance: 'steps.hint.acceptance',
  transfers: 'steps.hint.transfers',
  contain: 'steps.hint.contain',
};

export const STEP_ICON: Record<ResolutionOp, typeof Plane> = {
  rebooking: Plane,
  tickets: Ticket,
  hotels: BedDouble,
  vouchers: UtensilsCrossed,
  acceptance: UserCheck,
  transfers: Bus,
  contain: ShieldCheck,
};

/** Fallback for an op this build doesn't know (a newer BFF) — never an undefined component. */
export function stepIcon(op: string): typeof Plane {
  return STEP_ICON[op as ResolutionOp] ?? HelpCircle;
}
export function stepLabel(op: string, t: RebookingT = rebookingT): string {
  const key = STEP_LABEL_KEY[op as ResolutionOp];
  return key ? t(key) : op;
}

/** Status chip tone — token-themed. `labelKey`, never a label: the chip is
 *  rendered by `StatusChip`, which resolves it with its own `t`. */
export const STATUS_CHIP: Record<
  ResolutionStepStatus,
  { labelKey: string; className: string; icon: typeof CircleDashed }
> = {
  PENDING: { labelKey: 'steps.chip.PENDING', className: 'bg-muted text-muted-foreground', icon: CircleDashed },
  RUNNING: {
    labelKey: 'steps.chip.RUNNING',
    className: 'bg-sky-100 text-sky-700 dark:bg-sky-950/50 dark:text-sky-300',
    icon: Loader2,
  },
  DONE: {
    labelKey: 'steps.chip.DONE',
    className: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950/50 dark:text-emerald-300',
    icon: CheckCircle2,
  },
  PARTIAL: {
    labelKey: 'steps.chip.PARTIAL',
    className: 'bg-amber-100 text-amber-800 dark:bg-amber-950/50 dark:text-amber-300',
    icon: TriangleAlert,
  },
  BLOCKED: { labelKey: 'steps.chip.BLOCKED', className: 'bg-muted text-muted-foreground', icon: Lock },
  SKIPPED: { labelKey: 'steps.notApplicable', className: 'bg-muted text-muted-foreground', icon: MinusCircle },
  UNKNOWN: {
    labelKey: 'steps.state.unknown.label',
    className: 'bg-destructive/10 text-destructive',
    icon: HelpCircle,
  },
};

/** Per-op relabel of a SKIPPED chip — "hotels skipped" is a POSITIVE fact the
 *  operator must read ("not needed"), not a greyed-out "n/a". */
const SKIPPED_CHIP_BY_OP: Partial<Record<ResolutionOp, { labelKey: string; className: string }>> = {
  hotels: {
    labelKey: 'steps.notNeeded',
    className: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950/50 dark:text-emerald-300',
  },
};

/** A rebooking the station's policy turned off: legible, and its own colour — it is
 *  neither an achievement (emerald) nor a greyed non-event. */
const POLICY_SKIPPED_CHIP = {
  labelKey: 'steps.offByPolicy',
  className: 'bg-indigo-100 text-indigo-700 dark:bg-indigo-950/50 dark:text-indigo-300',
};

/** The step shape these two read — spelled out rather than `Pick`ed so a caller
 *  may pass an explicit `policyDisabled: undefined` (exactOptionalPropertyTypes). */
interface ISkippedLike {
  op: ResolutionOp;
  status: ResolutionStepStatus;
  policyDisabled?: boolean | undefined;
}

/** The chip tone a SKIPPED step should wear instead of the generic "Not
 *  applicable", or `undefined` when the generic greyed chip is right. */
function skippedChipTone(step: ISkippedLike): { labelKey: string; className: string } | undefined {
  if (step.status !== 'SKIPPED') return undefined;
  if (step.op === 'rebooking' && step.policyDisabled) return POLICY_SKIPPED_CHIP;
  return SKIPPED_CHIP_BY_OP[step.op];
}

/** As above, with the label resolved for rendering. */
export function skippedChip(
  step: ISkippedLike,
  t: RebookingT = rebookingT,
): { label: string; className: string } | undefined {
  const tone = skippedChipTone(step);
  return tone ? { label: t(tone.labelKey), className: tone.className } : undefined;
}

/** Whether a SKIPPED step is a fact worth READING (vs. a greyed n/a) — it keeps
 *  full opacity and a coloured chip. */
export function skippedIsPositive(step: ISkippedLike): boolean {
  return skippedChipTone(step) !== undefined;
}

/**
 * Pre-run rebooking policy gate (rebooking TDS §18 "opt-in by absence"): the
 * station's governing policy carries no rebooking block, so the engine is OFF
 * here and any run could only end POLICY_DISABLED for every group. Every surface
 * that offers a rebook action reads this and withholds it, naming the rule
 * instead — Tower, the Overview cockpit, the Passengers bulk bar, Command Center
 * V2 and the express board all answer from this one seam.
 */
export function rebookingPolicyGate(steps: readonly ResolutionStep[] | undefined): {
  disabled: boolean;
  policyUrn?: string;
} {
  const step = steps?.find((s) => s.op === 'rebooking');
  if (!step?.policyDisabled) return { disabled: false };
  return { disabled: true, ...(step.policyUrn ? { policyUrn: step.policyUrn } : {}) };
}

/** The gap noun a step counts its exceptions in — "12 no options", "3 no KYC". */
const GAP_KEY: Partial<Record<ResolutionOp, string>> = {
  rebooking: 'steps.counter.gap.noOptions',
  hotels: 'steps.counter.gap.noCapacity',
  acceptance: 'steps.counter.gap.declined',
  vouchers: 'steps.counter.gap.noKyc',
  transfers: 'steps.counter.gap.noShow',
};

/** "done/total" for a step, or "—/—" when unknown, or the skipped reason. */
export function stepCounter(step: ResolutionStep, t: RebookingT = rebookingT): string {
  if (step.status === 'UNKNOWN' || step.done == null || step.total == null) return '—/—';
  if (step.status === 'SKIPPED') {
    if (step.op === 'hotels') return t('steps.counter.nobodyOvernights');
    if (step.op === 'rebooking' && step.policyDisabled) return t('steps.counter.engineOff');
    return t('steps.counter.notApplicable');
  }
  const base = `${step.done}/${step.total}`;
  const extra: string[] = [];
  if ((step.gaps ?? 0) > 0) {
    extra.push(t(GAP_KEY[step.op] ?? 'steps.counter.gap.pending', { count: step.gaps ?? 0 }));
  }
  return extra.length ? `${base} · ${extra.join(' · ')}` : base;
}
